/**
 * Excel Export Service
 *
 * Generates multi-sheet Excel workbooks for bond calculator data export.
 * Includes sheets for: Dates, Taxable Values, Existing Debt, Bonds, Cash Flow, Tax Year Plan, Revenue Map
 */

import * as XLSX from 'xlsx';
import type { ProjectData } from '~/hooks/useProjectStore';
import type {
  BondSeries,
  Candidate,
  CashFlow,
  Configuration,
  DateRecord,
  ExistingDebtProject,
  RevenueMap,
  TaxableValueMap,
  TaxYearBasedProjectPlanRecord,
} from '~/types';

// ============================================
// Types
// ============================================

export interface ExcelExportOptions {
  projectName: string;
  configurationName?: string;
  millageShapeName?: string;
}

// ============================================
// Filename Generation (shared with CSV export)
// ============================================

/**
 * Convert a string to a URL-friendly slug
 */
const toSlug = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * Generate export filename with project-config-shape-export-datetime format
 */
const generateExportFilename = (options: ExcelExportOptions): string => {
  const parts: string[] = [];

  if (options.projectName) parts.push(toSlug(options.projectName));
  if (options.configurationName) parts.push(toSlug(options.configurationName));
  if (options.millageShapeName) parts.push(toSlug(options.millageShapeName));
  parts.push('export');

  // Add datetime to the minute (YYYY-MM-DD-HHmm)
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10);
  const timeStr = now.toTimeString().slice(0, 5).replace(':', '');
  parts.push(`${dateStr}-${timeStr}`);

  return `${parts.join('-')}.xlsx`;
};

// ============================================
// Sheet Generators
// ============================================

/**
 * Generate Dates sheet data
 */
const generateDatesSheet = (dateRecords: DateRecord[] | null): unknown[][] => {
  if (!dateRecords || dateRecords.length === 0) {
    return [['No date records available']];
  }

  const headers = [
    'Project Year',
    'Tax Year',
    'Payment Dates',
    'Fiscal Year End',
    'Last Payment of FY',
  ];

  const rows = [...dateRecords]
    .sort((a, b) => a.projectYear - b.projectYear)
    .map((record) => [
      record.projectYear,
      record.taxYear,
      record.paymentDates.join(', '),
      record.fiscalYearEndDate,
      record.lastPaymentDateOfFiscalYear,
    ]);

  return [headers, ...rows];
};

/**
 * Generate Taxable Values sheet data
 */
const generateTaxableValuesSheet = (taxableValueMap: TaxableValueMap | null): unknown[][] => {
  if (!taxableValueMap || Object.keys(taxableValueMap).length === 0) {
    return [['No taxable value data available']];
  }

  const headers = ['Tax Year', 'Taxable Value', 'Growth Rate (%)'];

  const rows = Object.values(taxableValueMap)
    .sort((a, b) => a.taxYear - b.taxYear)
    .map((entry) => [entry.taxYear, entry.taxableValue, (entry.growthRate * 100).toFixed(2)]);

  return [headers, ...rows];
};

/**
 * Generate Existing Debt sheet data
 */
const generateExistingDebtSheet = (
  existingDebtProjects: ExistingDebtProject[] | null
): unknown[][] => {
  if (!existingDebtProjects || existingDebtProjects.length === 0) {
    return [['No existing debt data available']];
  }

  const headers = [
    'Project Name',
    'Payment Date',
    'Principal',
    'Interest',
    'Total Payment',
    'Fiscal Year End',
  ];

  const rows: unknown[][] = [];

  for (const project of existingDebtProjects) {
    if (!project.existingDebtPaymentRecords) continue;

    for (const record of project.existingDebtPaymentRecords) {
      rows.push([
        project.name,
        record.paymentDate,
        record.principalPayment,
        record.totalPaymentInterest,
        record.totalPayment,
        record.fiscalYearInformation?.fiscalYearEndingDate || '',
      ]);
    }
  }

  // Sort by payment date
  rows.sort((a, b) => String(a[1]).localeCompare(String(b[1])));

  return [headers, ...rows];
};

/**
 * Generate Bond Payment Schedule sheet data for a specific bond
 */
const generateBondSheet = (
  bondSeries: BondSeries,
  cashFlows: CashFlow[]
): { sheetName: string; data: unknown[][] } => {
  const headers = [
    'Payment Date',
    'Tax Year',
    'Principal',
    'Principal (K)',
    'Interest Rate (%)',
    'New Interest',
    'Future Interest',
    'Total Interest',
    'Total Payment',
  ];

  const bondCashFlows = cashFlows
    .filter((cf) => cf.bondPayments?.some((bp) => bp.bondSeriesId === bondSeries.id))
    .sort((a, b) => a.paymentDate.localeCompare(b.paymentDate));

  const rows = bondCashFlows.map((cf) => {
    const bondPayment = cf.bondPayments?.find((bp) => bp.bondSeriesId === bondSeries.id);
    if (!bondPayment) {
      return [cf.paymentDate, cf.relatedTaxBillYear, 0, 0, 0, 0, 0, 0, 0];
    }

    return [
      cf.paymentDate,
      cf.relatedTaxBillYear,
      bondPayment.principal,
      Math.round(bondPayment.principal / 1000),
      (bondPayment.interestRate * 100).toFixed(3),
      bondPayment.newInterest,
      bondPayment.futureInterestObligation,
      bondPayment.totalInterest,
      bondPayment.totalPayment,
    ];
  });

  // Add totals row
  if (rows.length > 0) {
    const totals = rows.reduce(
      (acc, row) => {
        acc.principal += Number(row[2]) || 0;
        acc.newInterest += Number(row[5]) || 0;
        acc.futureInterest += Number(row[6]) || 0;
        acc.totalInterest += Number(row[7]) || 0;
        acc.totalPayment += Number(row[8]) || 0;
        return acc;
      },
      { principal: 0, newInterest: 0, futureInterest: 0, totalInterest: 0, totalPayment: 0 }
    );

    rows.push([
      'TOTAL',
      '',
      totals.principal,
      Math.round(totals.principal / 1000),
      '',
      totals.newInterest,
      totals.futureInterest,
      totals.totalInterest,
      totals.totalPayment,
    ]);
  }

  // Truncate sheet name to 31 chars (Excel limit)
  const sheetName = bondSeries.name.slice(0, 31);

  return { sheetName, data: [headers, ...rows] };
};

/**
 * Generate Cash Flow sheet data
 */
const generateCashFlowSheet = (cashFlows: CashFlow[] | null): unknown[][] => {
  if (!cashFlows || cashFlows.length === 0) {
    return [['No cash flow data available']];
  }

  const headers = [
    'Payment Date',
    'Tax Year',
    'Fiscal Year End',
    'Millage',
    'Revenue %',
    'Payment Revenue',
    'Existing Debt',
    'Revenue - Existing Debt',
    'Total Principal',
    'Total Principal (K)',
    'Total Interest',
    'Total Bond Payment',
    'DRF Adjustment',
    'DRF Balance',
  ];

  const sortedFlows = [...cashFlows].sort((a, b) => a.paymentDate.localeCompare(b.paymentDate));

  // Calculate totals from bond payments for each cash flow
  const getCashFlowTotals = (cf: CashFlow) => {
    const totalPrincipal = cf.bondPayments?.reduce((sum, bp) => sum + bp.principal, 0) || 0;
    const totalInterest = cf.bondPayments?.reduce((sum, bp) => sum + bp.totalInterest, 0) || 0;
    return { totalPrincipal, totalInterest };
  };

  const rows = sortedFlows.map((cf) => {
    const { totalPrincipal, totalInterest } = getCashFlowTotals(cf);
    return [
      cf.paymentDate,
      cf.relatedTaxBillYear,
      cf.relatedFiscalYearEndingDate,
      cf.millageRate.toFixed(2),
      (cf.paymentRevenuePercentage * 100).toFixed(1),
      cf.paymentRevenuePortion,
      cf.debtPaymentAmount,
      cf.revenueMinusExistingDebt,
      totalPrincipal,
      Math.round(totalPrincipal / 1000),
      totalInterest,
      cf.totalBondPayment,
      cf.drfAdjustment,
      cf.drfEndingBalance,
    ];
  });

  // Add totals row
  if (rows.length > 0) {
    const totals = sortedFlows.reduce(
      (acc, cf) => {
        const { totalPrincipal, totalInterest } = getCashFlowTotals(cf);
        acc.paymentRevenue += cf.paymentRevenuePortion;
        acc.existingDebt += cf.debtPaymentAmount;
        acc.revenueMinusDebt += cf.revenueMinusExistingDebt;
        acc.totalPrincipal += totalPrincipal;
        acc.totalInterest += totalInterest;
        acc.totalBondPayment += cf.totalBondPayment;
        acc.drfAdjustment += cf.drfAdjustment;
        return acc;
      },
      {
        paymentRevenue: 0,
        existingDebt: 0,
        revenueMinusDebt: 0,
        totalPrincipal: 0,
        totalInterest: 0,
        totalBondPayment: 0,
        drfAdjustment: 0,
      }
    );

    const lastFlow = sortedFlows[sortedFlows.length - 1];

    rows.push([
      'TOTAL',
      '',
      '',
      '',
      '',
      totals.paymentRevenue,
      totals.existingDebt,
      totals.revenueMinusDebt,
      totals.totalPrincipal,
      Math.round(totals.totalPrincipal / 1000),
      totals.totalInterest,
      totals.totalBondPayment,
      totals.drfAdjustment,
      lastFlow?.drfEndingBalance || 0,
    ]);
  }

  return [headers, ...rows];
};

/**
 * Generate Tax Year Plan sheet data
 */
const generateTaxYearPlanSheet = (
  taxYearPlan: TaxYearBasedProjectPlanRecord[] | null | undefined,
  bondNames: string[]
): unknown[][] => {
  if (!taxYearPlan || taxYearPlan.length === 0) {
    return [['No tax year plan data available']];
  }

  // Build headers with bond columns
  const bondHeaders = bondNames.flatMap((name) => [`${name} Principal`, `${name} Principal (K)`]);

  const headers = [
    'Tax Year',
    'Taxable Value',
    'Millage',
    'Total Revenue',
    'Existing Debt',
    'Net Revenue',
    ...bondHeaders,
    'Total Bond Payments',
    'Total Bond Payments (K)',
    'Total Payments',
    'DRF Adjustment',
    'DRF Ending Balance',
  ];

  const sortedPlan = [...taxYearPlan].sort((a, b) => a.taxYear - b.taxYear);

  const rows = sortedPlan.map((record) => {
    const bondPrincipalPairs = bondNames.flatMap((_, index) => {
      const principal = record.bondPayments?.[index] ?? 0;
      return [principal, Math.round(principal / 1000)];
    });

    return [
      record.taxYear,
      record.taxableValue,
      record.millage.toFixed(2),
      record.totalRevenue,
      record.existingDebt,
      record.netRevenue,
      ...bondPrincipalPairs,
      record.totalBondPayments,
      Math.round(record.totalBondPayments / 1000),
      record.totalPayment,
      record.drfAdjustment,
      record.drfEndingBalance,
    ];
  });

  // Add totals row
  if (rows.length > 0) {
    const totals = sortedPlan.reduce(
      (acc, record) => {
        acc.totalRevenue += record.totalRevenue;
        acc.existingDebt += record.existingDebt;
        acc.netRevenue += record.netRevenue;
        acc.totalBondPayments += record.totalBondPayments;
        acc.totalPayment += record.totalPayment;
        acc.drfAdjustment += record.drfAdjustment;

        // Sum bond payments by index
        bondNames.forEach((_, index) => {
          acc.bondPayments[index] =
            (acc.bondPayments[index] || 0) + (record.bondPayments?.[index] ?? 0);
        });

        return acc;
      },
      {
        totalRevenue: 0,
        existingDebt: 0,
        netRevenue: 0,
        totalBondPayments: 0,
        totalPayment: 0,
        drfAdjustment: 0,
        bondPayments: [] as number[],
      }
    );

    const bondTotalPairs = bondNames.flatMap((_, index) => {
      const total = totals.bondPayments[index] || 0;
      return [total, Math.round(total / 1000)];
    });

    const lastRecord = sortedPlan[sortedPlan.length - 1];

    rows.push([
      'TOTAL',
      '',
      '',
      totals.totalRevenue,
      totals.existingDebt,
      totals.netRevenue,
      ...bondTotalPairs,
      totals.totalBondPayments,
      Math.round(totals.totalBondPayments / 1000),
      totals.totalPayment,
      totals.drfAdjustment,
      lastRecord?.drfEndingBalance || 0,
    ]);
  }

  return [headers, ...rows];
};

/**
 * Generate Revenue Map sheet data
 */
const generateRevenueMapSheet = (revenueMap: RevenueMap | null): unknown[][] => {
  if (!revenueMap || Object.keys(revenueMap).length === 0) {
    return [['No revenue map data available']];
  }

  const headers = [
    'Tax Year',
    'Growth Rate (%)',
    'Taxable Value',
    'Millage',
    'Revenue',
    'Existing Debt',
    'Revenue - Existing Debt',
    'Total Accumulated Interest',
    'Available Revenue',
    'Revenue Capacity',
  ];

  const entries = Object.values(revenueMap).sort((a, b) => a.taxYear - b.taxYear);

  const rows = entries.map((entry) => [
    entry.taxYear,
    (entry.growthRate * 100).toFixed(2),
    entry.taxableValue,
    entry.millage.toFixed(2),
    entry.revenue,
    entry.existingDebt,
    entry.revenueMinusExistingDebt,
    entry.totalAccumulatedInterest,
    entry.availableRevenue,
    entry.revenueCapacity,
  ]);

  // Add totals row
  if (rows.length > 0) {
    const totals = entries.reduce(
      (acc, entry) => {
        acc.revenue += entry.revenue;
        acc.existingDebt += entry.existingDebt;
        acc.revenueMinusExisting += entry.revenueMinusExistingDebt;
        acc.availableRevenue += entry.availableRevenue;
        acc.revenueCapacity += entry.revenueCapacity;
        return acc;
      },
      {
        revenue: 0,
        existingDebt: 0,
        revenueMinusExisting: 0,
        availableRevenue: 0,
        revenueCapacity: 0,
      }
    );

    rows.push([
      'TOTAL',
      '',
      '',
      '',
      totals.revenue,
      totals.existingDebt,
      totals.revenueMinusExisting,
      '',
      totals.availableRevenue,
      totals.revenueCapacity,
    ]);
  }

  return [headers, ...rows];
};

// ============================================
// Main Export Function
// ============================================

/**
 * Export project data to Excel workbook with multiple sheets
 */
export const exportToExcel = (
  project: ProjectData,
  configuration: Configuration,
  candidate: Candidate | null,
  options: ExcelExportOptions
): void => {
  // Create workbook
  const workbook = XLSX.utils.book_new();

  // 1. Dates sheet
  const datesData = generateDatesSheet(project.dateRecords);
  const datesSheet = XLSX.utils.aoa_to_sheet(datesData);
  XLSX.utils.book_append_sheet(workbook, datesSheet, 'Dates');

  // 2. Taxable Values sheet
  const taxValuesData = generateTaxableValuesSheet(project.taxableValueMap);
  const taxValuesSheet = XLSX.utils.aoa_to_sheet(taxValuesData);
  XLSX.utils.book_append_sheet(workbook, taxValuesSheet, 'Taxable Values');

  // 3. Existing Debt sheet
  const debtData = generateExistingDebtSheet(project.existingDebtProjects);
  const debtSheet = XLSX.utils.aoa_to_sheet(debtData);
  XLSX.utils.book_append_sheet(workbook, debtSheet, 'Existing Debt');

  // Only include candidate-specific sheets if candidate is selected
  if (candidate) {
    // 4. Bond Payment Schedule sheets (one per bond)
    const bondSeries = configuration.bondSeries || [];
    for (const bond of bondSeries) {
      const { sheetName, data } = generateBondSheet(bond, candidate.cashFlows || []);
      const bondSheet = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, bondSheet, sheetName);
    }

    // 5. Cash Flow sheet
    const cashFlowData = generateCashFlowSheet(candidate.cashFlows);
    const cashFlowSheet = XLSX.utils.aoa_to_sheet(cashFlowData);
    XLSX.utils.book_append_sheet(workbook, cashFlowSheet, 'Cash Flow');

    // 6. Tax Year Plan sheet
    const bondNames = bondSeries.map((b) => b.name);
    const taxYearData = generateTaxYearPlanSheet(candidate.taxYearBasedProjectPlan, bondNames);
    const taxYearSheet = XLSX.utils.aoa_to_sheet(taxYearData);
    XLSX.utils.book_append_sheet(workbook, taxYearSheet, 'Tax Year Plan');

    // 7. Revenue Map sheet
    const revenueData = generateRevenueMapSheet(candidate.revenueMap);
    const revenueSheet = XLSX.utils.aoa_to_sheet(revenueData);
    XLSX.utils.book_append_sheet(workbook, revenueSheet, 'Revenue Map');
  }

  // Generate filename and trigger download
  const filename = generateExportFilename(options);
  XLSX.writeFile(workbook, filename);
};
